import { Box, BoxProps, Stack, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { useFormatShortcut } from 'src/utils/system';

type ShortcutTextProps = {
  shortcut: string;
  label?: string;
  /** `xs` is the inline key hint next to a button label (17px box, 10.5px bold). */
  size?: 'xs' | 'small' | 'medium' | 'large';
} & Omit<BoxProps, 'size'>;

export const ShortcutText = (props: ShortcutTextProps) => {
  const { shortcut, size = 'medium', label, sx, ...rest } = props;
  const theme = useTheme();
  const formatShortcut = useFormatShortcut();
  const keySx: SxProps<Theme> = {
    alignSelf: size === 'xs' ? 'center' : 'flex-start',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'inline-flex',
    borderRadius: '0.25rem',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: theme.palette.custom.OV.sys.outline,
    // Tints with the surrounding ink: legible on a dark tooltip in light mode and on a
    // light modal alike, which no single surface token manages.
    backgroundColor: 'color-mix(in srgb, currentColor 10%, transparent)',
    textAlign: 'center',
    ...(size === 'xs' && {
      fontSize: '0.65625rem',
      fontWeight: 700,
      // a fixed box and no inherited button letter-spacing keep the glyph optically centred
      minWidth: '1.0625rem',
      height: '1.0625rem',
      padding: '0 0.25rem',
      lineHeight: 1,
      letterSpacing: 0,
    }),
    ...(size === 'small' && {
      fontSize: '0.5rem',
      minWidth: '1.3rem',
      minHeight: '1.3rem',
      padding: '0.2rem 0.3rem',
      lineHeight: '0.2rem',
    }),
    ...(size === 'medium' && {
      fontSize: '0.75rem',
      minWidth: '1.6rem',
      minHeight: '1.6rem',
      padding: '0 0.4rem',
      lineHeight: '0.75rem',
    }),
    ...(size === 'large' && {
      fontSize: '1rem',
      minWidth: '2rem',
      padding: '0.3rem 0.4rem',
      lineHeight: '1rem',
    }),
  };
  return (
    <Stack direction="row" alignItems="center">
      {!!label && (
        <Typography variant="body2" fontSize="inherit">
          {label}
        </Typography>
      )}
      <Box
        component="span"
        ml={label ? 1 : 0}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- MUI sx-merge pattern
        sx={[keySx, ...(Array.isArray(sx) ? sx : [sx])]}
        {...rest}
      >
        {formatShortcut(shortcut)}
      </Box>
    </Stack>
  );
};
